/* eslint-disable no-undef */
/* eslint-disable no-console */
import Keycloak from 'keycloak-js';
import packageInfo from './package.json';
import { start, navigateToUrl, getMountedApps, addErrorHandler } from 'single-spa';
import { displayCookieBanner } from './src/utilities.js';
import { fetchIntercomUserData } from './src/services.js';
import { USER_ROLES, mapUserRole, isAuthorized } from './src/auth/index.js';
import { ROUTES } from './src/router/routes.config';
import { LAYOUT_VALUES, CONTENT_TYPE_ROUTES } from './src/router/enums';
import { ROUTES as ROUTES_GROWTH } from './src/router/growth.routes';
import { Router } from './src/router/index.js';
import {
	shouldRedirectUser,
	storeCurrentRoute,
	isRoleInPermissions,
} from './src/router/utilities.js';
import { registerApplications } from './src/initialization/registerApplications.js';
import {
	MonitoringAdapter,
	initIntercom,
	mockIntercom,
	mockSentry,
} from './src/initialization/analytics.js';
import {
	setErrorPage,
	removeErrorPage,
	pushNotificationError,
} from './src/initialization/error.js';
import { setupViewportInfo } from './src/initialization/viewportInfo.js';
import { getFlagrPromise, setupFlagr } from './src/initialization/flagr';
import { initHelpForm } from './src/initialization/helpForm';

import { ENVS } from './src/enums.js';
import { generateHeaders } from './src/initialization/utils.js';

const LOGOUT_TIMEOUT = 5000;

const isLocalDev = [ENVS.DEV, ENVS.LOCAL].includes(process.env.NODE_ENV);
const isProd = [ENVS.PROD, ENVS.PRODUCTION].includes(process.env.NODE_ENV);

const doNotTrack = window.location.href.includes('dnt=true');

// Outs out of mixpanel for this user
if (doNotTrack) {
	mixpanel.opt_out_tracking();
}

/**
 * Mock Mixpanel when in local development
 */
if (isLocalDev) {
	window.mixpanel = {
		track: function (eventName, eventProperties) {
			console.log('Mixpanel track event', eventName, eventProperties);
		},
		register: function (eventName, eventProperties) {
			console.log('Mixpanel register event', eventName, eventProperties);
		},
		opt_out_tracking: function () {
			console.log('Mixpanel opt out event');
		},
		identify: function () {
			console.log('Mixpanel identify');
		},
		people: {
			set: function () {
				console.log('Mixpanel people set');
			},
		},
	};
}

/**
 * Where we store the global API service provided by major-config.
 * This is done so that KC's onTokenExpired can update the API token.
 */
window.API = null;

/**
 * Where we store the factura API service provided by major-config.
 * This is done so that KC's onTokenExpired can update the API token.
 */
window.CUSTOMER_API = null;

/**
 * Initially define Sentry as null se we can then mock it
 * when Sentry cannot be loaded due to adblockers
 */
window.Sentry = null;

/**
 * Setup Keycloak config object
 */
const KEYCLOAK_CONFIG = {
	url: process.env.KEYCLOAK_URL,
	realm: process.env.KEYCLOAK_REALM,
	clientId: process.env.KEYCLOAK_CLIENT_ID,
	onLoad: 'login-required',
	promiseType: 'native',
	checkLoginIframe: false,
};

const keycloak = new Keycloak(KEYCLOAK_CONFIG);

/**
 * Programmatic redirects to https
 */
if (!isLocalDev && window.location.protocol.match('https') === null) {
	window.mixpanel.track('app_redirect_https');
	window.location = `https://${window.location.host}${window.location.pathname}`;
}

/**
 * Where we keep the store and actions provided by major-config.
 * This is done so that KC's onTokenExpired can update the API services' token.
 */
let STORE_ACTIONS = null;
let GlobalStore = null;
/**
 * Sets the token to the API uppon token refresh callback.
 */
keycloak.onTokenExpired = () => {
	keycloak.updateToken(1).then(() => {
		GlobalStore.dispatch(STORE_ACTIONS.setUserToken(keycloak.token));
	});
};

/**
 Initialize Keycloak
 */
keycloak
	.init({ onLoad: KEYCLOAK_CONFIG.onLoad })
	.then(async () => {
		/**
		 * Store elements we'll need later on
		 */
		const htmlWrapper = document.querySelector('html');
		const layoutWrapper = document.getElementById('secondaryLayoutWrapper');
		const loaderWrapper = document.getElementById('loaderWrapper');

		// Remove global loader
		layoutWrapper.classList.add('fade-out');
		setTimeout(() => {
			loaderWrapper.remove();
		}, 600);

		window.mixpanel.track('app_init');
		window.mixpanel.track('app_keycloak_success'); //TODO: remove this after changing mixpanel funnels

		let MajorConfig;
		let externalDepError;

		// load non-critical dependecies
		const [NProgress] = await Promise.allSettled([
			System.import('nprogress'),
			System.import('snackbar'),
			System.import('pubsub-js'),
		]).then((results) =>
			results.map((result) => {
				if (result.status === 'rejected') {
					externalDepError = result.value;
					return null;
				}

				return result.value;
			})
		);

		/**
		 * Initialize Sentry if we're able to load
		 * the dependency (adblockers may prevent loading).
		 */
		try {
			await Promise.all([System.import('@sentry/browser'), System.import('@sentry/vue')]);

			Sentry.init({
				environment: process.env.NODE_ENV,
				dsn: isProd ? process.env.SENTRY_URL : null,
				release: packageInfo.version,
			});

			Sentry.setContext('user', userEmailObj);

			Sentry.configureScope((scope) => {
				scope.setUser(userEmailObj);
			});
		} catch (err) {
			/**
			 * If there's an error importing Sentry (most likely due to adblock)
			 * then specify that we'll want to mock it before starting single-spa.
			 */
			mockSentry();
			pushNotificationError(
				'Ad-blocking add-ons prevent us to adequately monitor and troubleshoot the Portal. Please consider disabling such add-ons.',
				{ duration: 0 }
			);
		}

		if (externalDepError) {
			Sentry.captureException(new Error("Major couldn't load external dependencies"));
			if (!isProd) {
				console.error(err);
			}
		}

		/**
		 * Add error handling for app mounting/lifecycle errors
		 */
		addErrorHandler((err) => {
			Sentry.captureException(err);

			if (!isProd) {
				console.error('MAJOR :: addErrorHandler :: err', err);
			}

			const isGlobal = [
				'@unbabel/topnav',
				'@unbabel/sidenav',
				'@unbabel/major',
				'@unbabel/ui',
			].includes(err.appOrParcelName);

			setErrorPage(isGlobal);
		});

		// if any of these break, we can't load the app
		try {
			[MajorConfig] = await Promise.all([
				System.import('@unbabel/major-config'),
				System.import('@unbabel/ui'),
			]);
		} catch (err) {
			if (!isProd) {
				console.error(err);
			}
			pushNotificationError('An unknown error happened');
			Sentry.captureException(new Error("Critical dependencies couldn't be loaded"), 'error');
			setErrorPage(true);
			return;
		}

		const { tokenParsed } = keycloak;
		const userEmailObj = { email: tokenParsed.email };

		// identify user
		mixpanel.identify(tokenParsed.email);

		// registering superproperties
		mixpanel.register(userEmailObj);

		if (!('realm_access' in tokenParsed)) {
			window.mixpanel.track('app_keycloak_realm_access_missing');
			Sentry.captureException(
				new Error("'realm_access' key not found in tokenParsed object"),
				'error'
			);
			pushNotificationError("Error: 'realm_access' key not found in token.");
			setTimeout(keycloak.logout, LOGOUT_TIMEOUT);
			return;
		}

		/**
		 * Retrieve user roles
		 */
		const roles = mapUserRole(tokenParsed.realm_access.roles);

		if (roles.length === 0) {
			window.mixpanel.track('app_keycloak_roles_missing');
			// if roles are missing, it means user has no group assigned => has no access to any portal
			layoutWrapper.style.display = 'none';
			const errorPage = document.querySelector('#errorPage');
			const errorPageMsg = errorPage.querySelector('#errorPageMsg');
			const errorPageButton = errorPage.querySelector('#errorPageButton');
			errorPageMsg.innerHTML = `<p>There is no portal account associated with your email address.</p>
<p>If your company has an account with Unbabel
        please ask the account owner to add <strong>${tokenParsed.email}</strong> in the portal user management.</p>`;
			errorPage.classList.add('is-visible');
			errorPageButton.textContent = 'Log out';
			errorPageButton.addEventListener('click', () => {
				keycloak.logout();
			});
			return;
		}

		const {
			GlobalStore: Store,
			initBusSubscriptions,
			STORE_ACTIONS: TMP_STORE_ACTIONS,
			APP_STATES,
			MENU_STATES,
			API: TMP_API, // Assign temp API so we can then assign it to our high-level API variable
			CSS_CONFIGS,
			FLAGR_KEYS,
			FLAGR_FEATURE_KEYS,
			UTILS,
			ORIGINS_ENUM,
		} = MajorConfig;

		// This is simply done to be used in KC token refresh callback
		API = new TMP_API(process.env.VUE_APP_TRANTOR_API_ENDPOINT);
		CUSTOMER_API = new TMP_API(process.env.VUE_APP_CUSTOMER_API_ENDPOINT);
		STORE_ACTIONS = TMP_STORE_ACTIONS;
		GlobalStore = Store;

		GlobalStore.dispatch(STORE_ACTIONS.setKeycloak(keycloak));
		GlobalStore.dispatch(STORE_ACTIONS.setEnvironment(process.env.NODE_ENV));
		GlobalStore.dispatch(STORE_ACTIONS.restoreExperiments());

		/**
		 * Subdomain redirect flow for non implementation roles
		 */
		if (!isAuthorized(roles, USER_ROLES.IMPLEMENTATION)) {
			// Redirect if not local env and FORCE_SUBDOMAIN_REDIRECT is true
			if (!isLocalDev && process.env.FORCE_SUBDOMAIN_REDIRECT === 'true') {
				if ('host' in tokenParsed) {
					/**
					 * If location is not in host list AND user is not CSM role, redirect to first value of host array.
					 * CSM users must be assigned to specific groups to access subdomains.
					 */
					if (
						tokenParsed.host.indexOf(window.location.hostname) < 0 &&
						!isAuthorized(roles, USER_ROLES.CSM)
					) {
						// By default we redirect to the first available host value provided from KC
						const destination = `${window.location.protocol}//${tokenParsed.host[0]}${window.location.pathname}${window.location.search}`;
						window.mixpanel.track('app_redirect_host_domain');
						// Sentry, for visibility only
						Sentry.withScope((scope) => {
							scope.setExtra('from', window.location.hostname);
							scope.setExtra('destination', destination);
							Sentry.captureMessage('User redirected', 'info');
						});
						// Send user to new destinaiton
						window.location = destination;
					}
				} else if (!('host' in tokenParsed)) {
					window.mixpanel.track('app_keycloak_host_missing');
					Sentry.captureException(new Error("'host' key not found in tokenParsed object"), 'error');
					pushNotificationError("Error: 'host' key not found in token.");
					setTimeout(keycloak.logout, LOGOUT_TIMEOUT);
					return;
				} else {
					Sentry.withScope((scope) => {
						scope.setExtra('from', window.location.hostname);
						scope.setExtra('destination', destination);
						Sentry.captureException(new Error('Unknown error in subdomain redirect flow'), 'error');
					});
					pushNotificationError('An error occurred.');
					setTimeout(keycloak.logout, LOGOUT_TIMEOUT);
					return;
				}
			}
		}

		/**
		 * Request customerID from Trantor
		 */
		const customerResponse = await API.GET(`${process.env.VUE_APP_TRANTOR_API_ENDPOINT}/customers`, {
			headers: {
				Authorization: `Bearer ${keycloak.token}`,
			},
		}).catch((res) => {
			let message = 'An error has occurred.';
			switch (res?.response?.status) {
				case 500:
				case 503:
					message = 'A server error has occurred.';
					break;
				case 401:
					message = 'Error: unauthorized to access this page.';
					break;
				case 403:
					message = 'Error: access to this page is forbidden.';
					break;
			}
			window.mixpanel.track('app_trantor_error', {
				error: message,
			});
			Sentry.captureException(new Error(message), 'error');
			pushNotificationError(message);
			setTimeout(keycloak.logout, LOGOUT_TIMEOUT);
			return;
		});

		// Handle error responses
		if (!customerResponse) {
			Sentry.withScope((scope) => {
				scope.setExtra('destination', window.location.hostname);
				Sentry.captureMessage('Empty server response', 'info');
			});
			pushNotificationError('Error: empty server response.');
			window.mixpanel.track('app_trantor_error', {
				error: 'Empty server response',
			});
			setTimeout(keycloak.logout, LOGOUT_TIMEOUT);
			return;
		}
		if (customerResponse.data.length === 0) {
			Sentry.withScope((scope) => {
				scope.setExtra('destination', window.location.hostname);
				Sentry.captureMessage('User with no customer ID', 'info');
			});
			pushNotificationError('Error: no customer ID found for this user.');
			window.mixpanel.track('app_trantor_error', {
				error: 'No customer ID found',
			});
			setTimeout(keycloak.logout, LOGOUT_TIMEOUT);
			return;
		}

		/**
		 * Store customer data
		 */
		const customerData = customerResponse.data[0];

		// Define some user porperties
		const isTrial = customerData.is_self_service;
		const hasInternalRole = isRoleInPermissions(roles, [USER_ROLES.CSM, USER_ROLES.IMPLEMENTATION]);
		const isExternalLangOps = isRoleInPermissions(roles, [USER_ROLES.LANGOPS]);

		mixpanel.register({
			customer: customerData.name,
		});

		// Add customer data to Mixpanel
		mixpanel.people.set({
			$name: tokenParsed.name,
			$email: tokenParsed.email,
			$organization: customerData.name,
			$canonical: customerData.canonical_name,
			$roles: roles,
			$isTrial: isTrial,
			$hasInternalRole: hasInternalRole,
		});

		/**
		 * Get this customer's brands in
		 * order to determine content types.
		 */

		let yieldContentTypeError = false;

		const getBrands = API.GET(
			`${process.env.VUE_APP_TRANTOR_API_ENDPOINT}/brands/default/content_types`,
			generateHeaders(customerData.id, keycloak.token)
		).catch((res) => {
			Sentry.withScope(() => {
				Sentry.captureMessage('error_response', res);
				Sentry.captureException(new Error('Unable to retrieve content types'), 'error');
			});
			pushNotificationError('Error: Unable to retrieve content types.');
			yieldContentTypeError = true;
		});

		/**
		 * Get this customer's available origins,
		 * ex 'api', 'freshdesk', 'weborder', etc
		 */

		const getOrigins = API.GET(
			`/customer/origins`,
			generateHeaders(customerData.id, keycloak.token)
		).catch((res) => {
			Sentry.withScope(() => {
				Sentry.captureMessage('error_response', res);
				Sentry.captureException(new Error('Unable to retrieve origins'), 'error');
			});
		});

		const flagrEnums = { USER_ROLES, FLAGR_KEYS, FLAGR_FEATURE_KEYS, STORE_ACTIONS };
		const getFlagr = getFlagrPromise(
			roles,
			{ ...customerData, user_email: tokenParsed.email },
			flagrEnums
		);

		const [brands, customerOrigins, flagrResponse] = await Promise.allSettled([
			getBrands,
			getOrigins,
			getFlagr,
		]).then((results) =>
			results.map((result) => {
				if (result.status === 'rejected') {
					Sentry.withScope(() => {
						Sentry.captureMessage('error_response', result.value);
						Sentry.captureException(new Error('Unable to retrieve customer data'), 'error');
					});
					return null;
				}

				return result.value;
			})
		);

		if (customerOrigins.data.origin.length) {
			GlobalStore.dispatch(STORE_ACTIONS.setOrigins(customerOrigins.data.origin));
		}

		/**
		 * Init EventBus global subscriptions
		 */
		initBusSubscriptions(keycloak);

		/**
		 * Setup & handle loading indicator on app state change
		 */
		NProgress?.configure({ showSpinner: false });

		/**
		 * Subscribe to Redux store and listen for changes
		 */
		GlobalStore.subscribe(() => {
			const { appState, menuState, currentRoute } = GlobalStore.getState();

			// Controls layoutWrapper
			if (currentRoute.layout === LAYOUT_VALUES.FULLSCREEN) {
				htmlWrapper.classList.remove('collapsedTopnav');
				layoutWrapper.classList.add('collapsedSidenav');
			} else {
				htmlWrapper.classList.remove('collapsedTopnav');
				layoutWrapper.classList.remove('collapsedSidenav');
			}

			// Adds/removes the open menu class to manipulate layout
			switch (menuState) {
				case MENU_STATES.OPEN:
					layoutWrapper.classList.add('hasOpenMenu');
					break;

				default:
					layoutWrapper.classList.remove('hasOpenMenu');
					break;
			}

			// Handles displaying the top progress bar depending on app state
			switch (appState) {
				case APP_STATES.LOADING:
					NProgress?.start();
					break;

				default:
				case APP_STATES.IDLE:
				case APP_STATES.LOADED:
					NProgress?.done();
					break;
			}
		});

		const excludedApps = ['@unbabel/topnav', '@unbabel/sidenav'];
		/**
		 * Initialize route change listeners
		 */
		const initRouteListeners = () => {
			let currentApp = '';
			/**
			 * ## BEFORE ## route changes: stores the active mounted app,
			 * cancels active API requests from unmounted apps and
			 * validates that a user can access path.
			 */
			window.addEventListener('single-spa:before-routing-event', (e) => {
				removeErrorPage();
				const activeApp = e.detail.appsByNewStatus.MOUNTED[0];
				if (typeof activeApp !== 'undefined') {
					GlobalStore.dispatch(STORE_ACTIONS.setCurrentApp(activeApp));
				}
				/**
				 * Cancel any active API requests before switching apps
				 */
				API.cancelRequests();

				// Change app into loading state
				GlobalStore.dispatch(STORE_ACTIONS.setAppState(APP_STATES.LOADING));

				/**
				 * Determine if we should redirect the user based on their roles
				 */
				const roles = GlobalStore.getState().user.roles;
				if (shouldRedirectUser(GlobalRouter, roles)) {
					const redirectRoute = GlobalRouter.getRouteForRedirect(roles);
					GlobalStore.dispatch(STORE_ACTIONS.setCurrentRoute(redirectRoute));
					window.mixpanel.track('app_redirect_user_role', {
						roles,
						redirect_to: redirectRoute.path,
					});
					navigateToUrl(redirectRoute.path);
				}
			});

			/**
			 * * ## AFTER ## route changes
			 */
			window.addEventListener('single-spa:routing-event', () => {
				/**
				 * Check which app was mounted to log event on FullStory
				 */
				const mountedApps = getMountedApps();
				// Ignore navigation apps
				excludedApps.forEach((appName) => mountedApps.splice(mountedApps.indexOf(appName), 1));

				if (currentApp !== mountedApps[0]) {
					currentApp = mountedApps[0];

					if (isGrowthFeatureFlagEnabled) {
						window.mixpanel.track('nt_pageview', {
							nt_app: currentApp,
							nt_page_path: window.location.pathname,
							nt_is_growth: true,
							nt_user_id: GlobalStore.getState().user.email,
							nt_account_name: GlobalStore.getState().organization.name,
						});
					} else {
						window.mixpanel.track('app_navigation', { section: currentApp });
					}

					if (isProd && !doNotTrack) {
						MonitoringAdapter.trackEvent('user_navigated_section', {
							section: currentApp,
						});
					}
				}

				storeCurrentRoute(GlobalStore, GlobalRouter, STORE_ACTIONS);
				GlobalStore.dispatch(STORE_ACTIONS.setAppState(APP_STATES.LOADED));
			});
		};

		/**
		 * Before first-mount, store the current route,
		 * remove global loader and initialize route listeners.
		 */
		window.addEventListener('single-spa:before-first-mount', () => {
			// Store current route
			storeCurrentRoute(GlobalStore, GlobalRouter, STORE_ACTIONS);

			// Remove apps skeleton loader
			document.body.classList.add('reveal-apps');
			setTimeout(() => {
				document.body.classList.add('apps-mounted');
			}, 500);

			window.mixpanel.track('app_loaded');
			// Init single-spa listeners
			setTimeout(initRouteListeners, 1000);
		});

		/**
		 * When app states change (i.e. mounted), we want to get the activeApp
		 * just once. That is because routing-event listener isn't active initially
		 * so we check for the active app here.
		 */
		window.addEventListener(
			'single-spa:app-change',
			() => {
				const mountedApps = getMountedApps();
				excludedApps.forEach((appName) => mountedApps.splice(mountedApps.indexOf(appName), 1));
				const activeApp = mountedApps[0];
				GlobalStore.dispatch(STORE_ACTIONS.setCurrentApp(activeApp));
			},
			{
				once: true,
			}
		);

		// For now, logout user if none of the required roles are found.
		// TODO: send to an unauthorized page.
		if (roles.length === 0) {
			window.mixpanel.track('app_keycloak_unauthorized_role');
			Sentry.withScope((scope) => {
				scope.setExtra('from', window.location.hostname);
				Sentry.captureMessage('Unauthorized role access', 'info');
			});
			setTimeout(keycloak.logout, LOGOUT_TIMEOUT);
			return;
		}

		/**
		 * Set user data and store it
		 */
		const userData = {
			email: tokenParsed.email,
			name: tokenParsed.name,
			givenName: tokenParsed.given_name,
			familyName: tokenParsed.family_name,
			roles,
			keycloakToken: keycloak.token,
		};

		GlobalStore.dispatch(STORE_ACTIONS.setUser(userData));

		/**
		 * Request Flagr to evaluate features
		 */
		setupFlagr(flagrResponse, GlobalStore, flagrEnums);

		const hasAgentRole = isRoleInPermissions(roles, [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN]);

		const isMeterEnabled = GlobalStore.getState().flagr.features[FLAGR_FEATURE_KEYS.NX_METER_ENABLED];

		const isGrowthFeatureFlagEnabled =
			!!GlobalStore.getState().flagr.features[FLAGR_FEATURE_KEYS.NX_GROWTH];

		const CustomerService = new MajorConfig.CustomerService(
			customerData.id,
			generateHeaders(customerData.id, keycloak.token)
		);

		/**
		 * Get customer credits and subscription data if user is not an agent
		 */
		if (!hasAgentRole) {
			GlobalStore.dispatch(STORE_ACTIONS.setCustomerCredits({ value: [], loading: true }));
			const fetchOptions = {
				loadSubscriptions: true,
				onCreditsDone: (_, currency) => {
					if (isMeterEnabled) {
						if (currency) {
							GlobalStore.dispatch(STORE_ACTIONS.restorePreferredCurrency(currency));
						}
						GlobalStore.dispatch(STORE_ACTIONS.setVisibleCurrency(currency));
					}
				},
			};

			CustomerService.fetchCustomerCredits(fetchOptions).then(([error, credits]) => {
				GlobalStore.dispatch(STORE_ACTIONS.setCustomerCredits({ value: credits, loading: false }));

				if (error) {
					Sentry.withScope(() => {
						Sentry.captureMessage('error_response', error);
						Sentry.captureException(new Error('Unable to retrieve credits'), 'error');
					});
				}
			});
		}

		// disable german if flag is not enabled
		if (
			window.bablic &&
			bablic.languages &&
			!GlobalStore.getState().flagr.features[FLAGR_FEATURE_KEYS.NX_GERMAN]
		) {
			bablic.languages.hide('de');
		}

		// Store organization data to Store
		GlobalStore.dispatch(
			STORE_ACTIONS.setOrganization({
				name: customerData.name,
				id: customerData.id,
				canonical_name: customerData.canonical_name,
				core_usernames: customerData.core_usernames,
				is_self_service: customerData.is_self_service,
			})
		);

		/******** OVERRIDING ROUTES *********/
		/**
		 * Activate language routes depending
		 * on available content_types.
		 */
		try {
			const availableContentTypes = brands.data[0].content_types;
			if (availableContentTypes.length > 0) {
				ROUTES.QUALITY_REPORTING.ACTIVE = true;

				GlobalStore.dispatch(STORE_ACTIONS.setContentTypes(availableContentTypes));

				availableContentTypes.map((type) => {
					const routeKey = CONTENT_TYPE_ROUTES[type.name.toUpperCase()];
					if (typeof routeKey !== 'undefined') {
						ROUTES[routeKey].ACTIVE = true;
					}
				});
			} else {
				/**
				 * Catch case when there aren't any content types
				 */
				Sentry.withScope(() => {
					Sentry.captureMessage('No content types returned', 'info');
				});
				yieldContentTypeError = true;
			}
		} catch (err) {
			Sentry.withScope(() => {
				Sentry.captureMessage('error_content', err);
				Sentry.captureException(new Error('Unable to set language routes'), 'error');
			});

			pushNotificationError('Error: Unable to set languages routes.');
			yieldContentTypeError = true;
		}

		/**
		 * If we've had any error fetching the
		 * content types, simply don't render
		 * the languages route
		 */
		if (yieldContentTypeError) {
			ROUTES.QUALITY_REPORTING.ACTIVE = false;
		}

		/**
		 * Disable routes for trial customers
		 */
		ROUTES.PIPELINES.ACTIVE = isTrial || hasInternalRole;
		ROUTES.QUALITY_ANNOTATIONS.ACTIVE = isTrial || hasInternalRole || isExternalLangOps;
		ROUTES.TRANSLATION_MEMORIES.ACTIVE = isTrial || hasInternalRole;
		ROUTES.GLOSSARIES.ACTIVE = hasInternalRole;

		/**
		 * Disable flagr routes
		 */
		const { features } = GlobalStore.getState().flagr;
		const isTMV2Enabled = features[FLAGR_FEATURE_KEYS.NX_NEW_TRANSLATION_MEMORIES_APP];

		ROUTES.TRANSLATIONS_AUDITING.ACTIVE =
			features[FLAGR_FEATURE_KEYS.NX_TRANSLATION_AUDITING_VISIBLE];

		if (isTMV2Enabled) {
			ROUTES.TRANSLATION_MEMORIES_NEW.ACTIVE = true;
			ROUTES.TRANSLATION_MEMORIES.LABEL = 'Improve TMs';
		} else {
			ROUTES.TRANSLATION_MEMORIES_NEW.ACTIVE = false;
			ROUTES.TRANSLATION_MEMORIES.LABEL = 'Translation Memories';
		}

		// todo: remove after rollout
		if (
			!GlobalStore.getState().flagr.features[
				FLAGR_FEATURE_KEYS.NX_TRANSLATION_LIST_REPLACING_TRANSLATIONS_AUDITING
			]
		) {
			ROUTES.TRANSLATIONS_AUDITING.ICON = 'file-search';
			ROUTES.TRANSLATIONS_AUDITING.LABEL = 'Audits';
		}
		/**
		 * Disable projects app for customers without that origin
		 */
		const availableOrigins = Array.from(GlobalStore.getState().origins, (x) => x.name);

		ROUTES.PROJECTS.ACTIVE = availableOrigins.includes(ORIGINS_ENUM.project_api_int.ID);

		/**
		 * Manage reporting routes
		 */
		if (isMeterEnabled) {
			ROUTES.REPORTING.DISPLAY_CHILDREN = false;
			ROUTES.REPORTING.CHILDREN.unshift('REPORTING_COST');
			ROUTES.REPORTING_COST.ACTIVE = true;
			ROUTES.REPORTING.CHILDREN.unshift('REPORTING_SUMMARY');
			ROUTES.REPORTING_SUMMARY.ACTIVE = true;
		}

		/**
		 * Instantiate Router
		 */
		const GlobalRouter = isGrowthFeatureFlagEnabled ? new Router(ROUTES_GROWTH) : new Router(ROUTES);

		/**
		 * Redirect user to adequate route depending on permissions
		 */
		if (shouldRedirectUser(GlobalRouter, roles)) {
			const redirectRoute = GlobalRouter.getRouteForRedirect(roles);
			// If no route is returned, logout the user
			// This happens if there are no available routes for this role
			if (typeof redirectRoute === 'undefined' || !redirectRoute) {
				window.mixpanel.track('app_logout_no_redirect_role', {
					roles,
					redirect_to: redirectRoute.path,
				});
				Sentry.captureException(new Error('No valid route for given role'), 'error');
				pushNotificationError('Error: no valid route for given role.');
				setTimeout(keycloak.logout, LOGOUT_TIMEOUT);
				return;
			}
			window.mixpanel.track('app_redirect_user_role', {
				roles,
				redirect_to: redirectRoute.path,
			});
			GlobalStore.dispatch(STORE_ACTIONS.setCurrentRoute(redirectRoute));
			navigateToUrl(redirectRoute.path);
		}

		/**
		 * Configure API, including customer ID header
		 */
		API.setAuthToken(keycloak.token);
		API.setHeader('customer-id', customerData.id);

		/**
		 * Initialize store with viewport information for responsiveness purposes
		 */
		setupViewportInfo(GlobalStore, STORE_ACTIONS, CSS_CONFIGS.BREAKPOINTS, UTILS);

		// Once we have info on viewport, always start with closed menu for small viewports
		const { width, SM } = GlobalStore.getState().viewport;

		if (width <= SM) {
			GlobalStore.dispatch(STORE_ACTIONS.setMenuState(MENU_STATES.CLOSED));
		}

		/**
		 * Register each microfrontend
		 */
		registerApplications(GlobalStore, GlobalRouter, API, FLAGR_FEATURE_KEYS);

		/**
		 * Start Single-SPA
		 */
		start({
			urlRerouteOnly: true,
		});

		// Remove animation prevention class
		setTimeout(() => {
			layoutWrapper.classList.remove('no-transition');
			displayCookieBanner();
		}, 500);

		/**
		 * Initiate analytics tools
		 * (Only Intercom needs to be mocked because it is called by other apps.)
		 */
		if (isProd && !doNotTrack) {
			// Fullstory session recording
			//TODO: Remove if Datadog session monitoring remains.
			MonitoringAdapter.init();
			MonitoringAdapter.identify({
				name: `${userData.givenName} ${userData.familyName}`,
				email: userData.email,
				organization: customerData.name,
				canonical: customerData.canonical_name,
				roles: roles,
				isTrial: isTrial,
				hasInternalRole: hasInternalRole,
			});

			// Datadog session recording
			if (window.DD_RUM) {
				window.DD_RUM.setUser({
					name: `${userData.givenName} ${userData.familyName}`,
					email: userData.email,
					organization: customerData.name,
					is_self_service: customerData.is_self_service,
				});
				window.DD_RUM.startSessionReplayRecording();
			}

			// Intercom
			const userDataIntercom = fetchIntercomUserData(userData, customerData, keycloak, isTrial);
			initIntercom(userDataIntercom, customerData, keycloak);
		} else {
			mockIntercom();
		}

		/**
		 * Add zendesk widget for Growth
		 */
		if (isGrowthFeatureFlagEnabled) {
			const zendeskScript = document.createElement('script');
			zendeskScript.id = 'ze-snippet';
			zendeskScript.src =
				'https://static.zdassets.com/ekr/snippet.js?key=4fe3b9d4-79b5-445f-b265-8bd9dc8519b4';
			zendeskScript.async = true;

			document.head.appendChild(zendeskScript);
		}

		/**
		 * Initiate formcrafts forms developed in the old way
		 * (without the npm package)
		 */
		initHelpForm();
	})
	.catch((err) => {
		// if and uncaught error occured, we show an error page
		setErrorPage(true);

		if (!isProd) {
			console.error(err);
		}

		Sentry.withScope(() => {
			Sentry.captureMessage('error_content', err);
			Sentry.captureException(new Error('Critical :: Uncaught error in Major load'), 'error');
		});
	});
